import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { paymentApi } from '@/api/endpoints';
import { Alert, Loading } from '@/components/common/State';
import { formatWon } from '@/utils/format';
import { DEV_PAYMENT } from '@/utils/devPayment';

/**
 * 토스 결제창 성공 리다이렉트 처리 페이지.
 * 쿼리로 받은 paymentKey/orderId/amount 를 백엔드에 넘겨 최종 승인을 요청한다.
 */
export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<'confirming' | 'done' | 'error'>('confirming');
  const [message, setMessage] = useState('');
  const [devFallback, setDevFallback] = useState(false);
  const requested = useRef(false);

  const paymentKey = searchParams.get('paymentKey');
  const orderNo = searchParams.get('orderId');
  const amount = Number(searchParams.get('amount'));
  // 실패 화면에서 개발용 0원 처리를 마치고 넘어온 경우 승인 절차를 건너뛴다.
  const alreadyApproved = searchParams.get('dev') === '1';

  useEffect(() => {
    // StrictMode 에서 두 번 호출되지 않도록 막는다 (결제 승인은 멱등하지 않다).
    if (requested.current) return;
    requested.current = true;

    if (alreadyApproved) {
      setDevFallback(true);
      setStatus('done');
      return;
    }

    if (!paymentKey || !orderNo || !Number.isFinite(amount)) {
      setStatus('error');
      setMessage('결제 정보가 올바르지 않습니다.');
      return;
    }

    const pending = sessionStorage.getItem('gency03.pendingOrder');
    const cartItemIds: number[] = pending ? (JSON.parse(pending).cartItemIds ?? []) : [];

    const succeed = () => {
      sessionStorage.removeItem('gency03.pendingOrder');
      queryClient.invalidateQueries({ queryKey: ['cart'] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      setStatus('done');
    };

    paymentApi
      .confirm({ paymentKey, orderNo, amount, cartItemIds })
      .then(succeed)
      .catch((e: Error) => {
        // 개발 환경에서는 토스 승인이 실패해도 0원 결제 완료로 넘긴다.
        if (!DEV_PAYMENT) {
          setStatus('error');
          setMessage(e.message);
          return;
        }
        paymentApi
          .devApprove({ orderNo, cartItemIds })
          .then(() => {
            setDevFallback(true);
            succeed();
          })
          .catch(() => {
            setStatus('error');
            setMessage(e.message);
          });
      });
  }, [paymentKey, orderNo, amount, alreadyApproved, queryClient]);

  if (status === 'confirming') {
    return <Loading label="결제를 승인하는 중입니다. 창을 닫지 마세요…" />;
  }

  if (status === 'error') {
    return (
      <div className="state">
        <div className="state__title">결제 승인에 실패했습니다</div>
        <Alert tone="error">{message}</Alert>
        <Link to="/cart" className="btn btn--primary btn--sm">
          장바구니로 돌아가기
        </Link>
      </div>
    );
  }

  return (
    <div className="state">
      <div style={{ fontSize: 48 }}>🎉</div>
      <div className="state__title">결제가 완료되었습니다</div>
      {devFallback && (
        <Alert tone="info">개발 모드: 토스 승인이 실패해 0원 결제로 처리했습니다.</Alert>
      )}
      <p>
        주문번호 <strong>{orderNo}</strong>
        <br />
        결제 금액 <strong>{formatWon(devFallback ? 0 : amount)}</strong>
      </p>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 16 }}>
        <Link to="/orders" className="btn btn--primary btn--sm">
          주문 내역 보기
        </Link>
        <Link to="/products" className="btn btn--outline btn--sm">
          쇼핑 계속하기
        </Link>
      </div>
    </div>
  );
}
